import { ToolDefinition } from "../types";
import { useSkillsStore } from "../skills/skillsStore";

/**
 * `read_skill` — load the full markdown protocol for a named skill.
 *
 * The list of available skills is already advertised in every system prompt
 * (see systemPrompt.ts → buildSystemPrompt), so Kate doesn't need a separate
 * `list_skills` tool. She just calls this when she sees a question that
 * matches one of the skills in the prompt.
 *
 * Skills are loaded from `GET /api/ai/skills/:name` via the skills store,
 * which caches per-session. So calling `read_skill` twice for the same name
 * in one chat is essentially free.
 */
export const skillToolDefinitions: ToolDefinition[] = [
  {
    name: "read_skill",
    description:
      "Load the full step-by-step protocol for one of the named skills " +
      "advertised in the system prompt's 'Available skills' section. Use this " +
      "when the user's question matches a skill — read the protocol, then " +
      "follow it (asking the required clarifying questions first, then " +
      "running the SQL/Python/etc. it describes). Each skill is a curated " +
      "playbook for a recurring executive question, so following one is " +
      "almost always better than improvising.",
    input_schema: {
      type: "object",
      properties: {
        name: {
          type: "string",
          description:
            "The slug of the skill, e.g. 'weekly-fleet-review'. Must match " +
            "one of the names listed in 'Available skills'.",
        },
      },
      required: ["name"],
    },
  },
];

export async function executeReadSkill(
  input: Record<string, unknown>,
): Promise<unknown> {
  const name = typeof input.name === "string" ? input.name.trim() : "";
  if (!name) {
    return { error: "name is required" };
  }
  const result = await useSkillsStore.getState().fetchSkillContent(name);
  if ("error" in result) return { error: result.error };
  return {
    name: result.name,
    title: result.title,
    description: result.description,
    content: result.content,
  };
}
